import { useSelector } from "react-redux";
import { useLocation, useParams, useRouteMatch } from "react-router-dom";
import { AppTester } from "supporting";
import { useCallback, useMemo } from "react";

import { ApplicationShared, ProfileRole, bProfile, getContextFromAppName, useProfile } from "@brg/auth";
import { Team } from "@brg/api";

import { LinkTo } from "shared/constants/linkTo";
import { EDITOR_ROUTES } from "shared/navbars/ecosystemMenu/editorPaths";
import { useNotificationContext } from "../contexts/NotificationContext";
import { HsePolicies } from "shared/constants/HsePolicies";
import { ModerationType } from "../pages/teams/const/ModerationTypes";
import useExternalOrganization from "shared/pages/exploreHseContextsNew/useExternalOrganization";
import useAppTester from "shared/MobileMenu/hooks/useAppTester";
import { useTranslation } from "react-i18next";
import { hasDefaultChannel, hasParticipantChannel, hasPersonalChannel } from "shared/helpers/teamsStatus";
import { useChannelsContext } from "shared/contexts/ChannelsContext";
import { useGetFeedInfo } from "./api/useGetFeedInfo";
import { SaveUserLang } from "shared/components/__ADDONS__/__LANG__/Utils/GetUserLang";
import { setAppLang } from "shared/components/__ADDONS__/__LANG__/Slice";
import { ActiveCompetitionSelector } from "shared/reducers/CompetitionActionPanel";

// скрыта по таске BURG-8193
const IS_GAME_BUTTON_ENABLED = false;

const CONTEXTS_WITHOUT_CREATE_PROJECT = [
    "places",
    "ageytomesh",
    "education",
    'dafes',
    "dvadesign",
    "superpuper",
    "monalisatravels",
    "alshibaya",
];

const useNavbarSettings = () => {
    const location = useLocation();
    const activeCompetition = useSelector(ActiveCompetitionSelector);
    const profile = useProfile();
    const isGalllleryBooks = AppTester.isBooksGallllery();
    const { connection } = useNotificationContext();
    const { t, i18n } = useTranslation();

    const {
        isDvaDesign,
        isAgeytomesh,
        isHseAnimation,
        isMonaLisaTravels,
        isAlshibaya,
        isMediiia,
        isHSEDesign,
        isLectureMediiia,
        isProHSE,
        isDaMuseum,
        isStartHSE,
        isSuperPuper,
        isCreativeHSE,
        isGreenAward,
        isPromtWeekend,
        isProDeziiign,
        isLMSHSE,
        isBrnd,
        isBooksMain,
        isBooksGallllery,
        isKiiidsArt,
        isSchool,
        isLPUDesign,
        isAKI,
        isNewDafesSegment,
        isFtbl,
    } = useAppTester();

    const {
        channels,
        loading
    } = useChannelsContext();

    const [feedEnabled, feedLoading] = useGetFeedInfo();

    const isSpacePro = AppTester.isContext('s-pro');
    const isHSEMain = AppTester.isContext('hsedesign')
    const isHseSpace = AppTester.isContext('hsespace');
    const isHsetto = AppTester.isContext('s-hsetto')

    const isProfilePage = !!useRouteMatch(LinkTo.designer);
    const isTeamAdminPage = useRouteMatch('/teamAdmin')
    const isTeamPage = !!useRouteMatch("/teams/:id");
    const isBrandsPage = !!useRouteMatch("/brands");
    const isMainPage = !!useRouteMatch(LinkTo.explore)?.isExact;
    const isHSEBooks = !!useRouteMatch([
        LinkTo.hseBooks,
        "/hsebooks",
    ]);
    const isProjectPreview = useRouteMatch<{ id: string }>("/preview/:id");
    const isCompetition = useRouteMatch<{ id: string }>("/competition/:id");
    const isBookPreview = useRouteMatch<{ id: string }>("/books/book-preview/:id");
    const isBookProjectPreview = useRouteMatch<{ id: string }>("/books/preview/:id");
    const isTeamPreview = useRouteMatch<{ id: string }>('/team-preview/:id');
    const isTeamLgPreview = useRouteMatch<{ id: string }>('/team-longread-preview/:id');
    const isPreview = useRouteMatch<{ id: string }>([
        "/preview/:id",
        "/books/book-preview/:id",
        "/books/preview/:id",
        "/book-preview/:id",
        "/team-preview/:id",
        "/team-longread-preview/:id",
        LinkTo.coursePreview,
        LinkTo.previewTeamLongreadExternal
    ]);
    const isPlaybooksPage = useRouteMatch<{ id: string }>("/playbooks");
    const previewEntityId = isPreview?.params?.id;
    const isBooks = AppTester.isBooks() || isBooksMain || isBooksGallllery || isHSEBooks;
    const context = getContextFromAppName(ApplicationShared.application);


    const isCompetitions = location.pathname.startsWith(LinkTo.awards);
    const isEditPage = !!useRouteMatch(EDITOR_ROUTES);

    const { teamName } = useParams<{ teamName: string }>();

    const shouldNotSaveTeam = useMemo(() => isProfilePage && isHSEMain, [isProfilePage, isHSEMain])

    const { team } = useExternalOrganization(teamName, false, shouldNotSaveTeam) as Team | undefined;

    const isModulesLMSHSE = team?.moderationType == ModerationType.LMS || location.pathname?.includes('courses')

    const isLMS = isLMSHSE || team?.moderationType == ModerationType.LMS;

    const isModer = useMemo(() => profile && bProfile.isModer(profile), [profile]);
    const isJuniorModer = useMemo(() => profile?.rolesList?.includes("juniormoder"), [profile]);
    const isBriefModer = profile && profile.rolesList && Array.isArray(profile.rolesList)
        && (new ProfileRole(HsePolicies.access_brief_competitions)).containsIn(profile.rolesList);
    const isCompetitionModer = profile && profile.rolesList && Array.isArray(profile.rolesList)
        && (new ProfileRole(HsePolicies.access_competitions)).containsIn(profile.rolesList);
    const isTeamOwner = useMemo(() => {
        if (!profile || !team) return false;
        return team?.ownersIDs?.includes(profile?.accountId)
    }, [team, profile])
    const isTeacher = profile && bProfile.isEmployee(profile);

    const canCreateProject = useMemo(() => {
        if (isAKI) {
            return profile?.globalTypes?.includes('Creatoraki');
        }
        if (isHSEDesign) {
            //@ts-ignore
            const teamIsCanCreateProject = team?.isCanCreateProject;
            
            return (
                isProHSE ||
                isSpacePro ||
                isDaMuseum ||
                isHSEMain ||
                (isCompetitions && !isStartHSE) ||
                isCreativeHSE ||
                teamIsCanCreateProject
            );
        }

        if (isKiiidsArt) {
            return true
        }

        const shouldDisplayForSuperPuper = !isSuperPuper || isModer || isJuniorModer
        const isTeamEditor = isTeamPage && (isModer || isTeamOwner)
        //@ts-ignore
        const teamIsCanCreateProject = team?.isCanCreateProject;
        
        const teamCanCreateProject = team && teamIsCanCreateProject !== undefined
            ? teamIsCanCreateProject
            : (isTeamEditor || !isLMS);
        
        return shouldDisplayForSuperPuper && teamCanCreateProject;
    }, [isCompetitions, team, isHSEDesign, isProHSE, isSpacePro, isDaMuseum, isHSEMain, isStartHSE, isCreativeHSE, isSuperPuper, isModer, isJuniorModer, isTeamPage, isTeamOwner, isLMS]);

    const shouldHideCreateButton = useMemo(() => (
        isAgeytomesh ||
        isPreview ||
        isGreenAward ||
        isTeamAdminPage ||
        isEditPage ||
        isSchool ||
        isPromtWeekend ||
        (isNewDafesSegment && isBrandsPage) ||
        (isSpacePro && location.pathname?.includes('/competitionsAdmin')) ||
        (isGalllleryBooks && isCompetitions) ||
        CONTEXTS_WITHOUT_CREATE_PROJECT.includes(context) ||
        isFtbl
    ), [location, isAgeytomesh, isPreview, isGreenAward, isTeamAdminPage, isEditPage, isSchool, isPromtWeekend, isSpacePro, isNewDafesSegment, isBrandsPage, context, isFtbl])
    //todo понять, для чего это условие
    const shouldDisplayLMSButton = Boolean(!isTeamAdminPage && (!isLMS || (team && (isTeamOwner || isModer || isTeacher))))

    const shouldDisplayProDropdown = (isProDeziiign || isProHSE || isSpacePro) && !location.pathname.includes('/awards')
    const shouldDisplayPlaybookDropdown = isPlaybooksPage

    const canViewSuperGames = useMemo(() => (
        Array.isArray(profile?.rolesList) &&
        (new ProfileRole(HsePolicies.can_view_games)).containsIn(profile.rolesList)
    ), [profile]);

    const shouldDisplayNotifications = useMemo(() => {
        return profile && connection && !isDvaDesign && !isAgeytomesh && !isHseAnimation && !isMonaLisaTravels && !isAlshibaya && !isFtbl
    }, [profile, connection, isFtbl])


    const shouldDisplayLangSwitcher = useMemo(() => {
        return (
            !isLPUDesign &&
            !isGreenAward &&
            !isPromtWeekend
        );
    }, [isLPUDesign, isGreenAward, isPromtWeekend])

    const shouldDisplayGameButton = useMemo(() => {
        return IS_GAME_BUTTON_ENABLED &&
            (isModer || canViewSuperGames) &&
            isMediiia &&
            !isLectureMediiia
    }, [profile, connection])

    const shouldDisplayFeedButton = (
        !isGreenAward &&
        !isKiiidsArt &&
        !isFtbl &&
        !!feedEnabled &&
        !feedLoading
    )

    const shouldDisplayChannelButton = useMemo(() => {
        if (isKiiidsArt) return false
        if (loading || !profile || channels?.length === 0) return false
        return channels?.some((channel) => {
            return hasPersonalChannel(channel, profile) || 
                hasDefaultChannel(channel, profile) || 
                hasParticipantChannel(channel, profile)
        })
    }, [channels, profile, loading])

    const shouldDisplayEditorInterface = isEditPage && !activeCompetition?.competitionId;

    const createButtonTitle = useMemo(() => {
        if ((isBooks) && !isProHSE && !isProDeziiign) {
            return t('nav.create_button.create_book');
        }
        if (isModulesLMSHSE || isLMS && !isHsetto && !isSpacePro) {
            return t('nav.create_button.create_modul');
        }
        if (isKiiidsArt) {
            return t('nav.create_button.add_drawing');
        }
        if (isAKI) {
            return t('nav.create_button.add_work_kiiids');
        }
        if (isBrnd) {
            return t("nav.create_button.add_work");
        }
        return t('nav.create_button.create_project');
    }, [isCompetitions, t, isModulesLMSHSE, isBooks, isModer, isProHSE])

    const onLangSwitch = useCallback((lang?: string) => {
        if (!lang) {
            lang = i18n.language === 'ru' ? 'en' : 'ru';
        }

        SaveUserLang(lang);
        setAppLang(lang);
        i18n.changeLanguage(lang);
    }, [i18n]);

    return {
        shouldDisplayNotifications,
        shouldDisplayGameButton,
        shouldDisplayEditorInterface,
        shouldDisplayChannelButton,
        shouldDisplayFeedButton,
        shouldDisplayLMSButton,
        shouldDisplayLangSwitcher,
        isEditPage,
        shouldDisplayCreateButton: canCreateProject && !shouldHideCreateButton,
        shouldDisplayProDropdown,
        shouldDisplayPlaybookDropdown,
        createButtonTitle,
        onLangSwitch
    };
}

export default useNavbarSettings;import { useSelector } from "react-redux";
import { useLocation, useParams, useRouteMatch } from "react-router-dom";
import { AppTester } from "supporting";
import { useCallback, useMemo } from "react";

import { ApplicationShared, ProfileRole, bProfile, getContextFromAppName, useProfile } from "@brg/auth";
import { Team } from "@brg/api";

import { LinkTo } from "shared/constants/linkTo";
import { EDITOR_ROUTES } from "shared/navbars/ecosystemMenu/editorPaths";
import { useNotificationContext } from "../contexts/NotificationContext";
import { HsePolicies } from "shared/constants/HsePolicies";
import { ModerationType } from "../pages/teams/const/ModerationTypes";
import useExternalOrganization from "shared/pages/exploreHseContextsNew/useExternalOrganization";
import useAppTester from "shared/MobileMenu/hooks/useAppTester";
import { useTranslation } from "react-i18next";
import { hasDefaultChannel, hasParticipantChannel, hasPersonalChannel } from "shared/helpers/teamsStatus";
import { useChannelsContext } from "shared/contexts/ChannelsContext";
import { useGetFeedInfo } from "./api/useGetFeedInfo";
import { SaveUserLang } from "shared/components/__ADDONS__/__LANG__/Utils/GetUserLang";
import { setAppLang } from "shared/components/__ADDONS__/__LANG__/Slice";
import { ActiveCompetitionSelector } from "shared/reducers/CompetitionActionPanel";

// скрыта по таске BURG-8193
const IS_GAME_BUTTON_ENABLED = false;

const CONTEXTS_WITHOUT_CREATE_PROJECT = [
    "places",
    "ageytomesh",
    "education",
    'dafes',
    "dvadesign",
    "superpuper",
    "monalisatravels",
    "alshibaya",
];

const useNavbarSettings = () => {
    const location = useLocation();
    const activeCompetition = useSelector(ActiveCompetitionSelector);
    const profile = useProfile();
    const isGalllleryBooks = AppTester.isBooksGallllery();
    const { connection } = useNotificationContext();
    const { t, i18n } = useTranslation();

    const {
        isDvaDesign,
        isAgeytomesh,
        isHseAnimation,
        isMonaLisaTravels,
        isAlshibaya,
        isMediiia,
        isHSEDesign,
        isLectureMediiia,
        isProHSE,
        isDaMuseum,
        isStartHSE,
        isSuperPuper,
        isCreativeHSE,
        isGreenAward,
        isPromtWeekend,
        isProDeziiign,
        isLMSHSE,
        isBrnd,
        isBooksMain,
        isBooksGallllery,
        isKiiidsArt,
        isSchool,
        isLPUDesign,
        isAKI,
        isNewDafesSegment,
        isFtbl,
    } = useAppTester();

    const {
        channels,
        loading
    } = useChannelsContext();

    const [feedEnabled, feedLoading] = useGetFeedInfo();

    const isSpacePro = AppTester.isContext('s-pro');
    const isHSEMain = AppTester.isContext('hsedesign')
    const isHseSpace = AppTester.isContext('hsespace');
    const isHsetto = AppTester.isContext('s-hsetto')

    const isProfilePage = !!useRouteMatch(LinkTo.designer);
    const isTeamAdminPage = useRouteMatch('/teamAdmin')
    const isTeamPage = !!useRouteMatch("/teams/:id");
    const isBrandsPage = !!useRouteMatch("/brands");
    const isMainPage = !!useRouteMatch(LinkTo.explore)?.isExact;
    const isHSEBooks = !!useRouteMatch([
        LinkTo.hseBooks,
        "/hsebooks",
    ]);
    const isProjectPreview = useRouteMatch<{ id: string }>("/preview/:id");
    const isCompetition = useRouteMatch<{ id: string }>("/competition/:id");
    const isBookPreview = useRouteMatch<{ id: string }>("/books/book-preview/:id");
    const isBookProjectPreview = useRouteMatch<{ id: string }>("/books/preview/:id");
    const isTeamPreview = useRouteMatch<{ id: string }>('/team-preview/:id');
    const isTeamLgPreview = useRouteMatch<{ id: string }>('/team-longread-preview/:id');
    const isPreview = useRouteMatch<{ id: string }>([
        "/preview/:id",
        "/books/book-preview/:id",
        "/books/preview/:id",
        "/book-preview/:id",
        "/team-preview/:id",
        "/team-longread-preview/:id",
        LinkTo.coursePreview,
        LinkTo.previewTeamLongreadExternal
    ]);
    const isPlaybooksPage = useRouteMatch<{ id: string }>("/playbooks");
    const previewEntityId = isPreview?.params?.id;
    const isBooks = AppTester.isBooks() || isBooksMain || isBooksGallllery || isHSEBooks;
    const context = getContextFromAppName(ApplicationShared.application);


    const isCompetitions = location.pathname.startsWith(LinkTo.awards);
    const isEditPage = !!useRouteMatch(EDITOR_ROUTES);

    const { teamName } = useParams<{ teamName: string }>();

    const shouldNotSaveTeam = useMemo(() => isProfilePage && isHSEMain, [isProfilePage, isHSEMain])

    const { team } = useExternalOrganization(teamName, false, shouldNotSaveTeam) as Team | undefined;

    const isModulesLMSHSE = team?.moderationType == ModerationType.LMS || location.pathname?.includes('courses')

    const isLMS = isLMSHSE || team?.moderationType == ModerationType.LMS;

    const isModer = useMemo(() => profile && bProfile.isModer(profile), [profile]);
    const isJuniorModer = useMemo(() => profile?.rolesList?.includes("juniormoder"), [profile]);
    const isBriefModer = profile && profile.rolesList && Array.isArray(profile.rolesList)
        && (new ProfileRole(HsePolicies.access_brief_competitions)).containsIn(profile.rolesList);
    const isCompetitionModer = profile && profile.rolesList && Array.isArray(profile.rolesList)
        && (new ProfileRole(HsePolicies.access_competitions)).containsIn(profile.rolesList);
    const isTeamOwner = useMemo(() => {
        if (!profile || !team) return false;
        return team?.ownersIDs?.includes(profile?.accountId)
    }, [team, profile])
    const isTeacher = profile && bProfile.isEmployee(profile);

    const canCreateProject = useMemo(() => {
        if (isAKI) {
            return profile?.globalTypes?.includes('Creatoraki');
        }
        if (isHSEDesign) {
            //@ts-ignore
            const teamIsCanCreateProject = team?.isCanCreateProject;
            
            return (
                isProHSE ||
                isSpacePro ||
                isDaMuseum ||
                isHSEMain ||
                (isCompetitions && !isStartHSE) ||
                isCreativeHSE ||
                teamIsCanCreateProject
            );
        }

        if (isKiiidsArt) {
            return true
        }

        const shouldDisplayForSuperPuper = !isSuperPuper || isModer || isJuniorModer
        const isTeamEditor = isTeamPage && (isModer || isTeamOwner)
        //@ts-ignore
        const teamIsCanCreateProject = team?.isCanCreateProject;
        
        const teamCanCreateProject = team && teamIsCanCreateProject !== undefined
            ? teamIsCanCreateProject
            : (isTeamEditor || !isLMS);
        
        return shouldDisplayForSuperPuper && teamCanCreateProject;
    }, [isCompetitions, team, isHSEDesign, isProHSE, isSpacePro, isDaMuseum, isHSEMain, isStartHSE, isCreativeHSE, isSuperPuper, isModer, isJuniorModer, isTeamPage, isTeamOwner, isLMS]);

    const shouldHideCreateButton = useMemo(() => (
        isAgeytomesh ||
        isPreview ||
        isGreenAward ||
        isTeamAdminPage ||
        isEditPage ||
        isSchool ||
        isPromtWeekend ||
        (isNewDafesSegment && isBrandsPage) ||
        (isSpacePro && location.pathname?.includes('/competitionsAdmin')) ||
        (isGalllleryBooks && isCompetitions) ||
        CONTEXTS_WITHOUT_CREATE_PROJECT.includes(context) ||
        isFtbl
    ), [location, isAgeytomesh, isPreview, isGreenAward, isTeamAdminPage, isEditPage, isSchool, isPromtWeekend, isSpacePro, isNewDafesSegment, isBrandsPage, context, isFtbl])
    //todo понять, для чего это условие
    const shouldDisplayLMSButton = Boolean(!isTeamAdminPage && (!isLMS || (team && (isTeamOwner || isModer || isTeacher))))

    const shouldDisplayProDropdown = (isProDeziiign || isProHSE || isSpacePro) && !location.pathname.includes('/awards')
    const shouldDisplayPlaybookDropdown = isPlaybooksPage

    const canViewSuperGames = useMemo(() => (
        Array.isArray(profile?.rolesList) &&
        (new ProfileRole(HsePolicies.can_view_games)).containsIn(profile.rolesList)
    ), [profile]);

    const shouldDisplayNotifications = useMemo(() => {
        return profile && connection && !isDvaDesign && !isAgeytomesh && !isHseAnimation && !isMonaLisaTravels && !isAlshibaya && !isFtbl
    }, [profile, connection, isFtbl])


    const shouldDisplayLangSwitcher = useMemo(() => {
        return (
            !isLPUDesign &&
            !isGreenAward &&
            !isPromtWeekend
        );
    }, [isLPUDesign, isGreenAward, isPromtWeekend])

    const shouldDisplayGameButton = useMemo(() => {
        return IS_GAME_BUTTON_ENABLED &&
            (isModer || canViewSuperGames) &&
            isMediiia &&
            !isLectureMediiia
    }, [profile, connection])

    const shouldDisplayFeedButton = (
        !isGreenAward &&
        !isKiiidsArt &&
        !isFtbl &&
        !!feedEnabled &&
        !feedLoading
    )

    const shouldDisplayChannelButton = useMemo(() => {
        if (isKiiidsArt) return false
        if (loading || !profile || channels?.length === 0) return false
        return channels?.some((channel) => {
            return hasPersonalChannel(channel, profile) || 
                hasDefaultChannel(channel, profile) || 
                hasParticipantChannel(channel, profile)
        })
    }, [channels, profile, loading])

    const shouldDisplayEditorInterface = isEditPage && !activeCompetition?.competitionId;

    const createButtonTitle = useMemo(() => {
        if ((isBooks) && !isProHSE && !isProDeziiign) {
            return t('nav.create_button.create_book');
        }
        if (isModulesLMSHSE || isLMS && !isHsetto && !isSpacePro) {
            return t('nav.create_button.create_modul');
        }
        if (isKiiidsArt) {
            return t('nav.create_button.add_drawing');
        }
        if (isAKI) {
            return t('nav.create_button.add_work_kiiids');
        }
        if (isBrnd) {
            return t("nav.create_button.add_work");
        }
        return t('nav.create_button.create_project');
    }, [isCompetitions, t, isModulesLMSHSE, isBooks, isModer, isProHSE])

    const onLangSwitch = useCallback((lang?: string) => {
        if (!lang) {
            lang = i18n.language === 'ru' ? 'en' : 'ru';
        }

        SaveUserLang(lang);
        setAppLang(lang);
        i18n.changeLanguage(lang);
    }, [i18n]);

    return {
        shouldDisplayNotifications,
        shouldDisplayGameButton,
        shouldDisplayEditorInterface,
        shouldDisplayChannelButton,
        shouldDisplayFeedButton,
        shouldDisplayLMSButton,
        shouldDisplayLangSwitcher,
        isEditPage,
        shouldDisplayCreateButton: canCreateProject && !shouldHideCreateButton,
        shouldDisplayProDropdown,
        shouldDisplayPlaybookDropdown,
        createButtonTitle,
        onLangSwitch
    };
}

export default useNavbarSettings;